import { useMutation } from "convex/react";
import { api } from "../../../../../convex/_generated/api";
import type { Id } from "../../../../../convex/_generated/dataModel";

export interface DecisionsMutations {
  resolve: (args: {
    humanTaskId: Id<"humanTasks">;
    resolution?: unknown;
  }) => Promise<{ ok: true }>;

  snooze: (args: {
    humanTaskId: Id<"humanTasks">;
    until: number;
  }) => Promise<{ ok: true; snoozedUntil: number }>;

  respond: (args: {
    humanTaskId: Id<"humanTasks">;
    payload: string;
  }) => Promise<{ ok: true }>;
}

/**
 * Named mutations hook for the Decisions tray.
 *
 * Returns callable wrappers for the three humanTasks mutations:
 * resolve, snooze, and respond.
 *
 * These callables are stable across renders (useMutation guarantees
 * referential stability of the returned function). The wrapper object
 * { resolve, snooze, respond } is a new object reference on every render,
 * but its contained functions are stable — safe to pass as props to child
 * components without useCallback wrappers in the parent.
 */
export function useDecisionsMutations(): DecisionsMutations {
  const resolve = useMutation(api.hitl.tasks.resolveTask);
  const snooze = useMutation(api.hitl.tasks.snooze);
  const respond = useMutation(api.hitl.tasks.respond);

  return { resolve, snooze, respond };
}
